import { useCallback, useMemo } from 'react'
import { CustomSelectBase } from '../../../forms/CustomSelect'
import { CustomSelectSectionSearchbarDropDown, CustomSelectSectionsProps } from '../../../forms/CustomSelect/CustomSelectSections'
import { DisplayTextFuncTy } from '../../../forms/CustomSelect/types'
import { Modify } from '../../../utils/globals/types'
import { CustomerPayload } from '../../../utils/reducers/customers/customer'
import { parseSearchCustomerProjectToSections } from '../SearcherCustomerProject/functions'
import { ItemCustomSelectDropDownRowSection } from '../../../forms/CustomSelect/CustomSelectSections/types'
import { getUserPermissions } from '../../../utils/reducers/getters'
import { permissionCheck } from '../../../baseComponents/PermissionChecker/function'
import useCustomerTypesDict from '../../../utils/hooks/customer/useCustomerTypesDict'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { faUser } from '@fortawesome/pro-light-svg-icons'

type CustomSelectSectionCustomersProps = Modify<CustomSelectSectionsProps, CustomSelectSectionCustomersLogicProps & {
    items?: never
}>


const CustomSelectSectionCustomers: React.FC<CustomSelectSectionCustomersProps> = ({customers, ...rest}) => {
    const { customersSectionItems, displayTextFunc } = useCustomSelectSectionCustomersLogic({ customers })
    return (
        <CustomSelectBase {...rest} items={customersSectionItems.customersSectionItems} DropDownComponent={CustomSelectSectionSearchbarDropDown} displayTextFunc={displayTextFunc} />
    )
}

interface CustomSelectSectionCustomersLogicProps {
    customers: CustomerPayload
}

const useCustomSelectSectionCustomersLogic = ({ customers }: CustomSelectSectionCustomersLogicProps) => {
    const { dictCustomerTypeTrans } = useCustomerTypesDict()
    const customersSectionItems = useMemo(() => {
        return parseSearchCustomerProjectToSections(customers, dictCustomerTypeTrans)
    }, [customers, dictCustomerTypeTrans])

    const displayTextFunc: DisplayTextFuncTy = useCallback((_items, value) => {
        return customersSectionItems.indexCustomers[value]?.text || customersSectionItems.indexCustomers[value as any]
    }, [customersSectionItems.indexCustomers])

    return { displayTextFunc, customersSectionItems }
}

type DropDownSearchBarAndCreateSectionCustomerProps = Modify<CustomSelectSectionsProps, {
    customers: CustomerPayload
    items?: never
    label?: never
}>

export const DropDownSearchBarAndCreateSectionCustomer: React.FC<DropDownSearchBarAndCreateSectionCustomerProps> = ({ customers, ...rest }) => {
    const { customersSectionItems } = useCustomSelectSectionCustomersLogic({ customers })
    const { parsedCustomersSectionItems } = useCustomSelectAndCreateSectionCustomerLogic({ customersSectionItems: customersSectionItems.customersSectionItems })
    return (
        <CustomSelectSectionSearchbarDropDown items={parsedCustomersSectionItems} onChange={rest.onChange as any} value={rest.value || ''} {...rest} />
    )
}

type CustomSelectAndCreateSectionCustomerProps = Modify<CustomSelectSectionsProps, {
    customers: CustomerPayload
    items?: never
}>

export const CustomSelectAndCreateSectionCustomer: React.FC<CustomSelectAndCreateSectionCustomerProps> = ({ customers, ...rest }) => {
    const { customersSectionItems, displayTextFunc } = useCustomSelectSectionCustomersLogic({ customers })
    const { parsedCustomersSectionItems } = useCustomSelectAndCreateSectionCustomerLogic({ customersSectionItems: customersSectionItems.customersSectionItems })
    return (
        <CustomSelectBase {...rest} items={parsedCustomersSectionItems} DropDownComponent={CustomSelectSectionSearchbarDropDown} displayTextFunc={displayTextFunc} />
    )
}

interface CustomSelectAndCreateSectionCustomerLogicProps {
    customersSectionItems: ItemCustomSelectDropDownRowSection[];
}

const useCustomSelectAndCreateSectionCustomerLogic = ({ customersSectionItems }: CustomSelectAndCreateSectionCustomerLogicProps) => {
    const { t } = useTwinTranslation()
    
    const othersTrans = t('create', 'Crear')
    const createOtherTrans = t('createCustomer', 'Crear cliente')
    const parsedCustomersSectionItems = useMemo(() => {
        const userPermissions = getUserPermissions('customer.profile.main')
        if (permissionCheck(userPermissions, 'create')) {
            customersSectionItems.splice(0, 0, { value: 0, text: createOtherTrans, hiddenText: createOtherTrans, icon: faUser, className: 'section_items_background' })
            customersSectionItems.splice(0, 0, { value: "", text: othersTrans, isSection: true })
        }
        return customersSectionItems
    }, [othersTrans, createOtherTrans, customersSectionItems])

    return { parsedCustomersSectionItems }
}


export default CustomSelectSectionCustomers